// 復元時の Document 検証。型アサーションだけに頼らず、構造・参照・数値を
// すべて確認してから受け入れる (参照プロジェクトの教訓: `{}` すら通っていた)。
// 1 件でも不正があれば例外を投げ、呼び出し側は現在の文書に触れない。

import {
  BLEND_MODES,
  MAX_DOCUMENT_DIMENSION,
  type BlendMode,
  type Document,
  type GroupBlendMode,
  type Layer,
  type LayerMask,
  type Adjustment,
  type FillPaint,
  type LayerStyle,
  type ShapeGeometry
} from '@/types/document'
import type { WarpStyle, SelectiveColorRange } from '@/types/document'
import { SELECTIVE_COLOR_RANGES } from '@/types/document'
import { defaultAdjustment, ADJUSTMENT_KINDS } from './defaults'

const ID_RE = /^[\w-]+$/
const FORBIDDEN_KEYS = new Set(['__proto__', 'constructor', 'prototype'])

export class DocumentValidationError extends Error {
  override name = 'DocumentValidationError'
}

function fail(msg: string): never {
  throw new DocumentValidationError(`文書の検証に失敗: ${msg}`)
}

function isObj(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v)
}

function num(v: unknown, name: string, min = -Infinity, max = Infinity): number {
  if (typeof v !== 'number' || !Number.isFinite(v)) fail(`${name} が数値ではない`)
  if (v < min || v > max) fail(`${name} が範囲外: ${v}`)
  return v
}

function id(v: unknown, name: string): string {
  if (typeof v !== 'string' || !ID_RE.test(v) || FORBIDDEN_KEYS.has(v)) fail(`${name} の ID が不正: ${String(v)}`)
  return v
}

function bool(v: unknown, name: string): boolean {
  if (typeof v !== 'boolean') fail(`${name} が真偽値ではない`)
  return v
}

function str(v: unknown, name: string, maxLen = 1000): string {
  if (typeof v !== 'string' || v.length > maxLen) fail(`${name} が文字列ではないか長すぎる`)
  return v
}

function isBlendMode(v: string): v is BlendMode {
  return (BLEND_MODES as readonly string[]).includes(v)
}

function parseMask(mk: unknown, key: string): LayerMask {
  if (!isObj(mk) || !isObj(mk.offset)) fail(`レイヤー ${key} の mask が不正`)
  return {
    pixelId: id(mk.pixelId, 'mask.pixelId'),
    enabled: bool(mk.enabled, 'mask.enabled'),
    linked: bool(mk.linked, 'mask.linked'),
    density: num(mk.density, 'mask.density', 0, 1),
    feather: num(mk.feather, 'mask.feather', 0, 10000),
    offset: { x: num(mk.offset.x, 'mask.offset.x'), y: num(mk.offset.y, 'mask.offset.y') }
  }
}

const HEX_RE = /^#[0-9a-fA-F]{6}$/

function hex(v: unknown, name: string): string {
  if (typeof v !== 'string' || !HEX_RE.test(v)) fail(`${name} が #rrggbb ではない`)
  return v
}

function levels(v: unknown, name: string) {
  if (!isObj(v)) fail(`${name} が不正`)
  return {
    inBlack: num(v.inBlack, `${name}.inBlack`, 0, 255), inWhite: num(v.inWhite, `${name}.inWhite`, 0, 255), gamma: num(v.gamma, `${name}.gamma`, 0.01, 10),
    outBlack: num(v.outBlack, `${name}.outBlack`, 0, 255), outWhite: num(v.outWhite, `${name}.outWhite`, 0, 255)
  }
}

function curve(v: unknown, name: string): [number, number][] {
  if (!Array.isArray(v) || v.length < 2) fail(`${name} が不正`)
  return v.map((p: unknown) => {
    if (!Array.isArray(p) || p.length !== 2) fail(`${name} の点が不正`)
    return [num(p[0], `${name}.x`, 0, 255), num(p[1], `${name}.y`, 0, 255)] as [number, number]
  })
}

function tri(v: unknown, name: string, min: number, max: number): [number, number, number] {
  if (!Array.isArray(v) || v.length !== 3) fail(`${name} が不正`)
  return [num(v[0], name, min, max), num(v[1], name, min, max), num(v[2], name, min, max)]
}

/** 調整パラメータの検証 (種別ごと)。既定値で欠けたキーを補う */
export function validateAdjustment(raw: unknown): Adjustment {
  if (!isObj(raw) || typeof raw.kind !== 'string' || !(ADJUSTMENT_KINDS as string[]).includes(raw.kind)) fail('調整の種別が不正')
  const kind = raw.kind as Adjustment['kind']
  const d = defaultAdjustment(kind)
  switch (kind) {
    case 'levels': {
      const out: Adjustment = { kind, rgb: levels(raw.rgb ?? (d as { rgb: unknown }).rgb, 'levels.rgb') }
      for (const c of ['r', 'g', 'b'] as const) if (raw[c] !== undefined) out[c] = levels(raw[c], `levels.${c}`)
      return out
    }
    case 'curves': {
      const out: Adjustment = { kind, rgb: curve(raw.rgb ?? (d as { rgb: unknown }).rgb, 'curves.rgb') }
      for (const c of ['r', 'g', 'b'] as const) if (raw[c] !== undefined) out[c] = curve(raw[c], `curves.${c}`)
      return out
    }
    case 'brightness-contrast': return { kind, brightness: num(raw.brightness ?? 0, 'brightness', -150, 150), contrast: num(raw.contrast ?? 0, 'contrast', -100, 100) }
    case 'exposure': return { kind, exposure: num(raw.exposure ?? 0, 'exposure', -20, 20), offset: num(raw.offset ?? 0, 'offset', -0.5, 0.5), gamma: num(raw.gamma ?? 1, 'gamma', 0.01, 10) }
    case 'hue-saturation': {
      const m = isObj(raw.master) ? raw.master : {}
      return {
        kind,
        master: { hue: num(m.hue ?? 0, 'hue', -180, 180), saturation: num(m.saturation ?? 0, 'saturation', -100, 100), lightness: num(m.lightness ?? 0, 'lightness', -100, 100) },
        colorize: !!raw.colorize, colorizeHue: num(raw.colorizeHue ?? 0, 'colorizeHue', 0, 360), colorizeSaturation: num(raw.colorizeSaturation ?? 25, 'colorizeSaturation', 0, 100), colorizeLightness: num(raw.colorizeLightness ?? 0, 'colorizeLightness', -100, 100)
      }
    }
    case 'color-balance': return { kind, shadows: tri(raw.shadows ?? [0, 0, 0], 'shadows', -100, 100), midtones: tri(raw.midtones ?? [0, 0, 0], 'midtones', -100, 100), highlights: tri(raw.highlights ?? [0, 0, 0], 'highlights', -100, 100), preserveLuminosity: raw.preserveLuminosity !== false }
    case 'black-white': return { kind, reds: num(raw.reds ?? 40, 'reds', -200, 300), yellows: num(raw.yellows ?? 60, 'yellows', -200, 300), greens: num(raw.greens ?? 40, 'greens', -200, 300), cyans: num(raw.cyans ?? 60, 'cyans', -200, 300), blues: num(raw.blues ?? 20, 'blues', -200, 300), magentas: num(raw.magentas ?? 80, 'magentas', -200, 300), tint: !!raw.tint, tintHex: raw.tintHex === undefined ? '#e6c896' : hex(raw.tintHex, 'tintHex') }
    case 'photo-filter': return { kind, hex: raw.hex === undefined ? '#ec8a00' : hex(raw.hex, 'photo-filter.hex'), density: num(raw.density ?? 25, 'density', 0, 100), preserveLuminosity: raw.preserveLuminosity !== false }
    case 'channel-mixer': {
      const row = (v: unknown, n: string): [number, number, number, number] => {
        if (!Array.isArray(v) || v.length !== 4) fail(`${n} が不正`)
        return [num(v[0], n, -200, 200), num(v[1], n, -200, 200), num(v[2], n, -200, 200), num(v[3], n, -200, 200)]
      }
      return { kind, r: row(raw.r ?? [100, 0, 0, 0], 'r'), g: row(raw.g ?? [0, 100, 0, 0], 'g'), b: row(raw.b ?? [0, 0, 100, 0], 'b'), monochrome: !!raw.monochrome }
    }
    case 'invert': return { kind }
    case 'posterize': return { kind, levels: num(raw.levels ?? 4, 'levels', 2, 255) }
    case 'threshold': return { kind, level: num(raw.level ?? 128, 'level', 1, 255) }
    case 'gradient-map': {
      if (!Array.isArray(raw.stops) || raw.stops.length < 1) fail('gradient-map.stops が不正')
      return { kind, stops: raw.stops.map((st: unknown) => { if (!isObj(st)) fail('stop が不正'); return { pos: num(st.pos, 'stop.pos', 0, 1), hex: hex(st.hex, 'stop.hex') } }), reverse: !!raw.reverse }
    }
    case 'vibrance': return { kind, vibrance: num(raw.vibrance ?? 0, 'vibrance', -100, 100), saturation: num(raw.saturation ?? 0, 'saturation', -100, 100) }
    case 'color-lookup': return { kind, lutPixelId: raw.lutPixelId === null || raw.lutPixelId === undefined ? null : id(raw.lutPixelId, 'color-lookup.lutPixelId'), size: num(raw.size ?? 2, 'color-lookup.size', 2, 256), name: str(raw.name ?? '', 'color-lookup.name', 200) }
    case 'selective-color': {
      const rs = isObj(raw.ranges) ? raw.ranges : {}
      const ranges = {} as Record<SelectiveColorRange, [number, number, number, number]>
      for (const name of SELECTIVE_COLOR_RANGES) {
        const v = (rs as Record<string, unknown>)[name]
        if (v === undefined) { ranges[name] = [0, 0, 0, 0]; continue }
        if (!Array.isArray(v) || v.length !== 4) fail(`selective-color.${name} は 4 要素の配列が必要`)
        ranges[name] = (v as unknown[]).map((x, i) => num(x, `selective-color.${name}[${i}]`, -100, 100)) as [number, number, number, number]
      }
      return { kind, ranges, relative: raw.relative === undefined ? true : !!raw.relative }
    }
  }
}

function blendOf(v: unknown, fallback: BlendMode): BlendMode {
  return typeof v === 'string' && isBlendMode(v) ? v : fallback
}

export function validateStyles(raw: unknown): LayerStyle {
  if (!isObj(raw)) fail('styles が不正')
  const out: LayerStyle = {}
  const shadow = (v: unknown, n: string) => {
    if (!isObj(v)) fail(`${n} が不正`)
    return { enabled: !!v.enabled, blendMode: blendOf(v.blendMode, 'multiply'), hex: hex(v.hex ?? '#000000', `${n}.hex`), opacity: num(v.opacity ?? 0.75, `${n}.opacity`, 0, 1), angle: num(v.angle ?? 120, `${n}.angle`, -360, 360), distance: num(v.distance ?? 5, `${n}.distance`, 0, 3000), spread: num(v.spread ?? 0, `${n}.spread`, 0, 100), size: num(v.size ?? 5, `${n}.size`, 0, 500) }
  }
  const glow = (v: unknown, n: string) => {
    if (!isObj(v)) fail(`${n} が不正`)
    const g: NonNullable<LayerStyle['outerGlow']> = { enabled: !!v.enabled, blendMode: blendOf(v.blendMode, 'screen'), hex: hex(v.hex ?? '#ffffbe', `${n}.hex`), opacity: num(v.opacity ?? 0.75, `${n}.opacity`, 0, 1), spread: num(v.spread ?? 0, `${n}.spread`, 0, 100), size: num(v.size ?? 5, `${n}.size`, 0, 500) }
    if (v.source === 'center' || v.source === 'edge') g.source = v.source
    return g
  }
  if (raw.dropShadow !== undefined) out.dropShadow = shadow(raw.dropShadow, 'dropShadow')
  if (raw.innerShadow !== undefined) out.innerShadow = shadow(raw.innerShadow, 'innerShadow')
  if (raw.outerGlow !== undefined) out.outerGlow = glow(raw.outerGlow, 'outerGlow')
  if (raw.innerGlow !== undefined) out.innerGlow = glow(raw.innerGlow, 'innerGlow')
  if (raw.bevel !== undefined) {
    const v = raw.bevel
    if (!isObj(v)) fail('bevel が不正')
    out.bevel = { enabled: !!v.enabled, style: v.style === 'outer' || v.style === 'emboss' ? v.style : 'inner', depth: num(v.depth ?? 100, 'bevel.depth', 1, 1000), size: num(v.size ?? 5, 'bevel.size', 0, 500), soften: num(v.soften ?? 0, 'bevel.soften', 0, 100), angle: num(v.angle ?? 120, 'bevel.angle', -360, 360), altitude: num(v.altitude ?? 30, 'bevel.altitude', 0, 90), highlightHex: hex(v.highlightHex ?? '#ffffff', 'bevel.highlightHex'), highlightOpacity: num(v.highlightOpacity ?? 0.75, 'bevel.highlightOpacity', 0, 1), shadowHex: hex(v.shadowHex ?? '#000000', 'bevel.shadowHex'), shadowOpacity: num(v.shadowOpacity ?? 0.75, 'bevel.shadowOpacity', 0, 1) }
  }
  if (raw.satin !== undefined) {
    const v = raw.satin
    if (!isObj(v)) fail('satin が不正')
    out.satin = { enabled: !!v.enabled, blendMode: blendOf(v.blendMode, 'multiply'), hex: hex(v.hex ?? '#000000', 'satin.hex'), opacity: num(v.opacity ?? 0.5, 'satin.opacity', 0, 1), angle: num(v.angle ?? 19, 'satin.angle', -360, 360), distance: num(v.distance ?? 11, 'satin.distance', 0, 500), size: num(v.size ?? 14, 'satin.size', 0, 500), invert: v.invert !== false }
  }
  const overlay = (v: unknown, n: string) => {
    if (!isObj(v)) fail(`${n} が不正`)
    const o: NonNullable<LayerStyle['colorOverlay']> = { enabled: !!v.enabled, blendMode: blendOf(v.blendMode, 'normal'), opacity: num(v.opacity ?? 1, `${n}.opacity`, 0, 1), hex: hex(v.hex ?? '#000000', `${n}.hex`) }
    if (Array.isArray(v.stops)) o.stops = v.stops.map((st: unknown) => { if (!isObj(st)) fail('stop が不正'); return { pos: num(st.pos, 'stop.pos', 0, 1), hex: hex(st.hex, 'stop.hex') } })
    if (v.angle !== undefined) o.angle = num(v.angle, `${n}.angle`, -360, 360)
    if (v.style === 'radial' || v.style === 'linear') o.style = v.style
    return o
  }
  if (raw.colorOverlay !== undefined) out.colorOverlay = overlay(raw.colorOverlay, 'colorOverlay')
  if (raw.gradientOverlay !== undefined) out.gradientOverlay = overlay(raw.gradientOverlay, 'gradientOverlay')
  if (raw.patternOverlay !== undefined) {
    const o = isObj(raw.patternOverlay) ? raw.patternOverlay : fail('patternOverlay が不正')
    out.patternOverlay = { enabled: !!o.enabled, blendMode: blendOf(o.blendMode, 'normal'), opacity: num(o.opacity ?? 1, 'patternOverlay.opacity', 0, 1), patternId: typeof o.patternId === 'string' ? o.patternId : '', scale: num(o.scale ?? 1, 'patternOverlay.scale', 0.05, 10) }
  }
  if (raw.stroke !== undefined) {
    const v = raw.stroke
    if (!isObj(v)) fail('stroke が不正')
    out.stroke = { enabled: !!v.enabled, size: num(v.size ?? 3, 'stroke.size', 0, 500), position: v.position === 'inside' || v.position === 'center' ? v.position : 'outside', blendMode: blendOf(v.blendMode, 'normal'), opacity: num(v.opacity ?? 1, 'stroke.opacity', 0, 1), hex: hex(v.hex ?? '#ff0000', 'stroke.hex') }
  }
  return out
}

export function validateGeometry(raw: unknown): ShapeGeometry {
  if (!isObj(raw)) fail('geometry が不正')
  const n = (k: string, min = -1e6, max = 1e6) => num(raw[k], `geometry.${k}`, min, max)
  switch (raw.type) {
    case 'rect': return { type: 'rect', x: n('x'), y: n('y'), w: n('w', 0), h: n('h', 0), radius: num(raw.radius ?? 0, 'radius', 0, 1e5) }
    case 'ellipse': return { type: 'ellipse', x: n('x'), y: n('y'), w: n('w', 0), h: n('h', 0) }
    case 'polygon': return { type: 'polygon', x: n('x'), y: n('y'), w: n('w', 0), h: n('h', 0), sides: num(raw.sides ?? 5, 'sides', 3, 100), star: num(raw.star ?? 0, 'star', 0, 0.99) }
    case 'line': return { type: 'line', x1: n('x1'), y1: n('y1'), x2: n('x2'), y2: n('y2') }
    case 'path': {
      if (!Array.isArray(raw.points)) fail('path.points が不正')
      return {
        type: 'path',
        closed: !!raw.closed,
        points: raw.points.map((p: unknown) => {
          if (!isObj(p)) fail('path の点が不正')
          const pt: { x: number; y: number; in?: { x: number; y: number }; out?: { x: number; y: number } } = { x: num(p.x, 'pt.x'), y: num(p.y, 'pt.y') }
          if (isObj(p.in)) pt.in = { x: num(p.in.x, 'in.x'), y: num(p.in.y, 'in.y') }
          if (isObj(p.out)) pt.out = { x: num(p.out.x, 'out.x'), y: num(p.out.y, 'out.y') }
          return pt
        })
      }
    }
  }
  fail(`geometry.type が不正: ${String(raw.type)}`)
}

function mat3(v: unknown, name: string): [number, number, number, number, number, number, number, number, number] {
  if (!Array.isArray(v) || v.length !== 9) fail(`${name} が 3x3 行列ではない`)
  return v.map((x: unknown, i: number) => num(x, `${name}[${i}]`)) as [number, number, number, number, number, number, number, number, number]
}

export function validateFill(raw: unknown): FillPaint {
  if (!isObj(raw)) fail('fill が不正')
  if (raw.kind === 'solid') return { kind: 'solid', hex: hex(raw.hex, 'fill.hex') }
  if (raw.kind === 'gradient') {
    const style = raw.style
    if (!['linear', 'radial', 'angle', 'reflected', 'diamond'].includes(style as string)) fail('fill.style が不正')
    if (!Array.isArray(raw.stops) || raw.stops.length < 1) fail('fill.stops が不正')
    return {
      kind: 'gradient',
      style: style as 'linear',
      stops: raw.stops.map((st: unknown) => { if (!isObj(st)) fail('stop が不正'); return { pos: num(st.pos, 'stop.pos', 0, 1), hex: hex(st.hex, 'stop.hex'), alpha: num(st.alpha ?? 1, 'stop.alpha', 0, 1) } }),
      angle: num(raw.angle ?? 90, 'fill.angle', -360, 360),
      scale: num(raw.scale ?? 1, 'fill.scale', 0.01, 10),
      reverse: !!raw.reverse
    }
  }
  if (raw.kind === 'pattern') {
    const off = isObj(raw.offset) ? raw.offset : { x: 0, y: 0 }
    return { kind: 'pattern', patternId: id(raw.patternId, 'fill.patternId'), scale: num(raw.scale ?? 1, 'fill.scale', 0.05, 10), offset: { x: num(off.x ?? 0, 'fill.offset.x'), y: num(off.y ?? 0, 'fill.offset.y') } }
  }
  fail('fill.kind が不正')
}

/**
 * 未知の JSON を Document として検証し、正規化したコピーを返す。
 * `knownPixelIds` を渡すと、参照している pixelId がすべて存在することも確認する。
 */
export function validateDocument(input: unknown, knownPixelIds?: Set<string>): Document {
  if (!isObj(input)) fail('オブジェクトではない')
  if (input.schemaVersion !== 1) fail(`未対応の schemaVersion: ${String(input.schemaVersion)}`)

  const m = input.meta
  if (!isObj(m)) fail('meta がない')
  const bg = m.background
  let background: Document['meta']['background']
  if (bg === 'transparent' || bg === 'white' || bg === 'black') background = bg
  else if (isObj(bg) && typeof bg.hex === 'string' && /^#[0-9a-fA-F]{6}$/.test(bg.hex)) background = { hex: bg.hex }
  else fail('meta.background が不正')
  const meta: Document['meta'] = {
    id: id(m.id, 'meta.id'),
    name: str(m.name, 'meta.name', 200),
    createdAt: num(m.createdAt, 'meta.createdAt', 0),
    updatedAt: num(m.updatedAt, 'meta.updatedAt', 0),
    width: num(m.width, 'meta.width', 1, MAX_DOCUMENT_DIMENSION),
    height: num(m.height, 'meta.height', 1, MAX_DOCUMENT_DIMENSION),
    dpi: num(m.dpi, 'meta.dpi', 1, 10000),
    colorProfile: 'sRGB',
    background
  }
  if (!Number.isInteger(meta.width) || !Number.isInteger(meta.height)) fail('サイズが整数ではない')

  if (!isObj(input.layers)) fail('layers がない')
  const layers: Record<string, Layer> = {}
  const pixelIds = new Set<string>()
  for (const [key, raw] of Object.entries(input.layers)) {
    id(key, 'layers のキー')
    if (!isObj(raw)) fail(`レイヤー ${key} がオブジェクトではない`)
    if (raw.id !== key) fail(`レイヤー ${key} の id が一致しない`)
    const lock = raw.lock
    if (!isObj(lock)) fail(`レイヤー ${key} の lock がない`)
    const base = {
      id: key,
      name: str(raw.name, `レイヤー ${key} の name`, 200),
      visible: bool(raw.visible, 'visible'),
      opacity: num(raw.opacity, 'opacity', 0, 1),
      fillOpacity: num(raw.fillOpacity, 'fillOpacity', 0, 1),
      lock: {
        all: bool(lock.all, 'lock.all'),
        pixels: bool(lock.pixels, 'lock.pixels'),
        position: bool(lock.position, 'lock.position'),
        transparency: bool(lock.transparency, 'lock.transparency')
      },
      clipToBelow: bool(raw.clipToBelow, 'clipToBelow')
    }
    const mask = raw.mask === undefined ? undefined : parseMask(raw.mask, key)
    if (mask) pixelIds.add(mask.pixelId)
    const labelColor = raw.labelColor === undefined ? undefined : raw.labelColor === null ? null : str(raw.labelColor, 'labelColor', 32)
    const blendMode = str(raw.blendMode, 'blendMode', 32)

    let layer: Layer
    if (raw.kind === 'raster') {
      if (!isBlendMode(blendMode)) fail(`ブレンドモードが不正: ${blendMode}`)
      const off = raw.offset
      if (!isObj(off)) fail(`レイヤー ${key} の offset がない`)
      const pixelId = id(raw.pixelId, 'pixelId')
      pixelIds.add(pixelId)
      layer = {
        ...base,
        kind: 'raster',
        blendMode,
        pixelId,
        offset: { x: num(off.x, 'offset.x'), y: num(off.y, 'offset.y') }
      }
    } else if (raw.kind === 'adjustment') {
      if (!isBlendMode(blendMode)) fail(`ブレンドモードが不正: ${blendMode}`)
      const adjustment = validateAdjustment(raw.adjustment)
      if (adjustment.kind === 'color-lookup' && adjustment.lutPixelId) pixelIds.add(adjustment.lutPixelId)
      layer = { ...base, kind: 'adjustment', blendMode, adjustment }
    } else if (raw.kind === 'fill') {
      if (!isBlendMode(blendMode)) fail(`ブレンドモードが不正: ${blendMode}`)
      layer = { ...base, kind: 'fill', blendMode, fill: validateFill(raw.fill) }
    } else if (raw.kind === 'text') {
      if (!isBlendMode(blendMode)) fail(`ブレンドモードが不正: ${blendMode}`)
      const st = raw.style
      if (!isObj(st)) fail(`テキストレイヤー ${key} の style がない`)
      const pos = raw.position
      if (!isObj(pos)) fail(`テキストレイヤー ${key} の position がない`)
      layer = {
        ...base,
        kind: 'text',
        blendMode,
        text: str(raw.text ?? '', 'text', 100000),
        style: {
          fontFamily: str(st.fontFamily ?? 'sans-serif', 'fontFamily', 200),
          fontSize: num(st.fontSize ?? 48, 'fontSize', 1, 5000),
          fontWeight: num(st.fontWeight ?? 400, 'fontWeight', 100, 900),
          italic: !!st.italic,
          hex: hex(st.hex ?? '#000000', 'text.hex'),
          lineHeight: num(st.lineHeight ?? 1.2, 'lineHeight', 0.1, 10),
          letterSpacing: num(st.letterSpacing ?? 0, 'letterSpacing', -1000, 1000),
          align: st.align === 'center' || st.align === 'right' ? st.align : 'left',
          orientation: st.orientation === 'vertical' ? 'vertical' : 'horizontal'
        },
        position: { x: num(pos.x, 'position.x'), y: num(pos.y, 'position.y') }
      }
      if (raw.boxWidth !== undefined) layer.boxWidth = num(raw.boxWidth, 'boxWidth', 1, 100000)
      if (raw.warp !== undefined && raw.warp !== null) {
        const wr = isObj(raw.warp) ? raw.warp : fail('warp が不正')
        const styles = ['arc', 'arcLower', 'arcUpper', 'bulge', 'flag', 'wave', 'fisheye', 'rise']
        if (!styles.includes(wr.style as string)) fail(`warp.style が不正: ${String(wr.style)}`)
        layer.warp = { style: wr.style as WarpStyle, bend: num(wr.bend ?? 0, 'warp.bend', -100, 100), hDist: num(wr.hDist ?? 0, 'warp.hDist', -100, 100), vDist: num(wr.vDist ?? 0, 'warp.vDist', -100, 100) }
      }
    } else if (raw.kind === 'shape') {
      if (!isBlendMode(blendMode)) fail(`ブレンドモードが不正: ${blendMode}`)
      layer = {
        ...base,
        kind: 'shape',
        blendMode,
        geometry: validateGeometry(raw.geometry),
        fillHex: raw.fillHex === null ? null : hex(raw.fillHex ?? '#000000', 'fillHex'),
        fillAlpha: num(raw.fillAlpha ?? 1, 'fillAlpha', 0, 1),
        strokeHex: raw.strokeHex === null || raw.strokeHex === undefined ? null : hex(raw.strokeHex, 'strokeHex'),
        strokeWidth: num(raw.strokeWidth ?? 2, 'strokeWidth', 0, 1000),
        strokeAlpha: num(raw.strokeAlpha ?? 1, 'strokeAlpha', 0, 1),
        strokeAlign: raw.strokeAlign === 'inside' || raw.strokeAlign === 'outside' ? raw.strokeAlign : 'center',
        transform: raw.transform === undefined ? [1, 0, 0, 0, 1, 0, 0, 0, 1] : mat3(raw.transform, 'transform')
      }
    } else if (raw.kind === 'smart') {
      if (!isBlendMode(blendMode)) fail(`ブレンドモードが不正: ${blendMode}`)
      const sr = isObj(raw.sourceRect) ? raw.sourceRect : fail('sourceRect が不正')
      const sourceId = id(raw.sourceId, 'sourceId')
      pixelIds.add(sourceId)
      layer = {
        ...base,
        kind: 'smart',
        blendMode,
        sourceId,
        sourceRect: { x: num(sr.x, 'sourceRect.x'), y: num(sr.y, 'sourceRect.y'), w: num(sr.w, 'sourceRect.w', 1, 1e6), h: num(sr.h, 'sourceRect.h', 1, 1e6) },
        transform: raw.transform === undefined ? [1, 0, 0, 0, 1, 0, 0, 0, 1] : mat3(raw.transform, 'transform'),
        interpolation: raw.interpolation === 'nearest' || raw.interpolation === 'bilinear' ? raw.interpolation : 'bicubic'
      }
    } else if (raw.kind === 'group') {
      if (blendMode !== 'pass-through' && !isBlendMode(blendMode)) fail(`ブレンドモードが不正: ${blendMode}`)
      if (!Array.isArray(raw.children)) fail(`グループ ${key} の children が配列ではない`)
      layer = {
        ...base,
        kind: 'group',
        blendMode: blendMode as GroupBlendMode,
        children: raw.children.map(c => id(c, 'children')),
        expanded: bool(raw.expanded, 'expanded')
      }
    } else {
      fail(`未知のレイヤー種別: ${String(raw.kind)}`)
    }
    if (mask) layer.mask = mask
    if (labelColor !== undefined) layer.labelColor = labelColor
    if (raw.styles !== undefined) layer.styles = validateStyles(raw.styles)
    if (raw.blendIf !== undefined && raw.blendIf !== null) {
      const bi = isObj(raw.blendIf) ? raw.blendIf : fail('blendIf が不正')
      const quad = (v: unknown, name: string): [number, number, number, number] => {
        if (!Array.isArray(v) || v.length !== 4) fail(`${name} は 4 要素の配列が必要`)
        const q = (v as unknown[]).map((x, i) => num(x, `${name}[${i}]`, 0, 255)) as [number, number, number, number]
        if (!(q[0] <= q[1] && q[1] <= q[2] && q[2] <= q[3])) fail(`${name} は昇順が必要`)
        return q
      }
      const ch = bi.channel
      if (ch !== 'gray' && ch !== 'r' && ch !== 'g' && ch !== 'b') fail('blendIf.channel が不正')
      layer.blendIf = { channel: ch, thisLayer: quad(bi.thisLayer ?? [0, 0, 255, 255], 'blendIf.thisLayer'), underlying: quad(bi.underlying ?? [0, 0, 255, 255], 'blendIf.underlying') }
    }
    layers[key] = layer
  }

  // ツリーの整合: 各レイヤーはちょうど 1 つの親を持ち、循環しない
  if (!Array.isArray(input.rootChildren)) fail('rootChildren が配列ではない')
  const rootChildren = input.rootChildren.map(c => id(c, 'rootChildren'))
  const seen = new Set<string>()
  const walk = (children: string[], depth: number) => {
    if (depth > 64) fail('レイヤーの入れ子が深すぎる')
    for (const c of children) {
      const l = layers[c]
      if (!l) fail(`存在しないレイヤーを参照: ${c}`)
      if (seen.has(c)) fail(`レイヤーが複数の親に属する: ${c}`)
      seen.add(c)
      if (l.kind === 'group') walk(l.children, depth + 1)
    }
  }
  walk(rootChildren, 0)
  for (const k of Object.keys(layers)) if (!seen.has(k)) fail(`ツリーに含まれないレイヤー: ${k}`)

  if (!Array.isArray(input.activeLayerIds)) fail('activeLayerIds が配列ではない')
  const activeLayerIds = input.activeLayerIds.map(c => id(c, 'activeLayerIds')).filter(c => layers[c])

  if (!isObj(input.selection)) fail('selection がない')
  const selPixel = input.selection.pixelId
  const selection: Document['selection'] = { pixelId: selPixel === null ? null : id(selPixel, 'selection.pixelId') }
  if (selection.pixelId) pixelIds.add(selection.pixelId)

  const patterns: Document['patterns'] = []
  if (input.patterns !== undefined) {
    if (!Array.isArray(input.patterns)) fail('patterns が配列ではない')
    for (const p of input.patterns) {
      if (!isObj(p)) fail('pattern が不正')
      const pt = { id: id(p.id, 'pattern.id'), name: str(p.name ?? 'パターン', 'pattern.name', 200), pixelId: id(p.pixelId, 'pattern.pixelId'), w: num(p.w, 'pattern.w', 1, 16384), h: num(p.h, 'pattern.h', 1, 16384) }
      pixelIds.add(pt.pixelId)
      patterns.push(pt)
    }
  }
  const comps: Document['comps'] = []
  if (input.comps !== undefined) {
    if (!Array.isArray(input.comps)) fail('comps が配列ではない')
    for (const c of input.comps) {
      if (!isObj(c)) fail('comp が不正')
      const states: Record<string, { visible: boolean; position?: { x: number; y: number }; styles?: LayerStyle | null }> = {}
      const raw = isObj(c.states) ? c.states : {}
      for (const [lid, st] of Object.entries(raw)) {
        if (!isObj(st) || !layers[lid]) continue
        const e: { visible: boolean; position?: { x: number; y: number }; styles?: LayerStyle | null } = { visible: !!st.visible }
        if (isObj(st.position)) e.position = { x: num(st.position.x, 'comp.position.x'), y: num(st.position.y, 'comp.position.y') }
        if (st.styles === null) e.styles = null
        else if (st.styles !== undefined) e.styles = validateStyles(st.styles)
        states[lid] = e
      }
      comps.push({ id: id(c.id, 'comp.id'), name: str(c.name ?? 'カンプ', 'comp.name', 200), states })
    }
  }
  const channels: Document['channels'] = []
  if (input.channels !== undefined) {
    if (!Array.isArray(input.channels)) fail('channels が配列ではない')
    for (const c of input.channels) {
      if (!isObj(c)) fail('channel が不正')
      const ch = { id: id(c.id, 'channel.id'), name: str(c.name ?? 'アルファ', 'channel.name', 200), pixelId: id(c.pixelId, 'channel.pixelId') }
      pixelIds.add(ch.pixelId)
      channels.push(ch)
    }
  }
  if (!Array.isArray(input.guides)) fail('guides が配列ではない')
  const guides: Document['guides'] = input.guides.map((g: unknown) => {
    if (!isObj(g)) fail('guide が不正')
    if (g.axis !== 'x' && g.axis !== 'y') fail('guide.axis が不正')
    return { id: id(g.id, 'guide.id'), axis: g.axis, position: num(g.position, 'guide.position') }
  })

  const pixelGeneration = num(input.pixelGeneration ?? 0, 'pixelGeneration', 0)

  if (knownPixelIds) {
    for (const p of pixelIds) if (!knownPixelIds.has(p)) fail(`ピクセルデータが欠けている: ${p}`)
  }

  return { schemaVersion: 1, meta, layers, rootChildren, activeLayerIds, selection, channels, patterns, comps, guides, pixelGeneration }
}
